/**
 * regulatoryBlockBuilder — transforme une fiche FDS Polyprint en un
 * ensemble de calques LabelForge prêts à être insérés dans le store.
 *
 * Convention :
 *   - Chaque calque généré porte la metadata `regBlock` qui le marque
 *     comme appartenant au bloc réglementaire d'un UFI précis.
 *   - `regBlock.blockId` est identique pour tous les calques d'un même
 *     import — permet de les retrouver groupés au moment du refresh.
 *   - `regBlock.role` distingue picto / signal-word / hazard / etc.
 *   - `contentLocked: true` empêche l'édition de texte (mais pas le
 *     déplacement, la rotation, ou la modification de mise en forme).
 *
 * Le rendu visuel utilise les types natifs (text / image / shape) sans
 * rien de spécifique — c'est la metadata qui change le comportement
 * dans la liste des calques (couleur orange) et l'inspecteur.
 *
 * Pictogrammes GHS : on privilégie les SVG officiels embarqués dans la
 * bibliothèque (data/shapeLibrary.js, ids 'official-ghs01' à 'official-ghs09')
 * plutôt que les PNG distants fournis par l'API. Avantages :
 *   - Zéro requête réseau supplémentaire pour les visuels
 *   - Rendu vectoriel net à n'importe quelle taille
 *   - Pas de risque de lien cassé si l'URL distante bouge
 *   - Couleurs officielles garanties (losange rouge + glyphe noir)
 * En fallback, si le code GHS n'est pas dans la bibliothèque (ex: code
 * exotique), on retombe sur l'image distante.
 */
import { hauteurTexteEstimee } from './hauteurTexte.js';
import { shapes as SHAPE_LIBRARY } from '../data/shapeLibrary';
// Le minimum légal d'un pictogramme vit dans regulatoryTemplate : une seule
// source de vérité. Ce module en avait sa propre copie (`pictoSize = 16`), et
// deux constantes égales qui peuvent diverger sont une divergence en attente.
import { PICTO_MIN_MM, POLICE_MENTIONS, CORPS_MENTIONS_PT } from './regulatoryTemplate';


/** Génère un blockId déterministe à partir de l'UFI (réutilisé au refresh). */
export function blockIdFromUfi(ufi) {
  const safe = (ufi || '').replace(/[^A-Z0-9]/gi, '').toLowerCase();
  return `reg-${safe}`;
}


/** BlockId pour un import par catalog (sans UFI) — basé sur client + gamme + produit. */
function blockIdFromCatalog(client, gamme, produit) {
  const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);
  return `reg-cat-${slug(client)}-${slug(gamme)}-${slug(produit)}`;
}


/**
 * Résout la variante d'étiquette d'un produit (ou d'une fiche UFI) pour un pays.
 *
 * Nouveau schéma : `labels` = dictionnaire { "FR": {…}, "CH": {…} } indexé par
 * code pays ISO. `label` (singulier) reste la variante par défaut (pays principal
 * du client). Priorité :
 *   1. variante du pays demandé (`labels[countryCode]`)
 *   2. `label` (variante par défaut, rétro-compatible anciens catalogues)
 *   3. 1ʳᵉ variante disponible dans `labels`
 *   4. null → produit sans étiquetage (ex: « Sans recette »)
 *
 * @param {object} source - un `produits_finis[i]` OU une réponse per-UFI
 * @param {string|null} countryCode - ex "FR", "CH"
 * @returns {object|null} l'objet label, ou null
 */
export function resolveLabelForCountry(source, countryCode, langCode = null) {
  if (!source) return null;
  const objet = (o) => (o && typeof o === 'object' && !Array.isArray(o)) ? o : null;
  const i18n = objet(source.labels_i18n);
  const labels = objet(source.labels);

  // 1. `labels_i18n[pays][langue]` — le plus précis. La langue demandée d'abord,
  //    puis celle que la fiche déclare elle-même, puis la première disponible :
  //    une variante de pays sans la langue demandée vaut mieux que rien.
  if (i18n) {
    const parPays = objet(i18n[countryCode]) || (countryCode ? null : objet(Object.values(i18n)[0]));
    if (parPays) {
      const langues = [langCode, source.label?.language, 'fr'].filter(Boolean);
      for (const l of langues) if (objet(parPays[l])) return parPays[l];
      const premiere = Object.values(parPays).find(objet);
      if (premiere) return premiere;
    }
  }
  // 2. `labels[pays]` — schéma précédent, toujours servi.
  if (labels && countryCode && objet(labels[countryCode])) return labels[countryCode];
  // 3. `label` — variante par défaut.
  if (source.label) return source.label;
  // 4. Faute de mieux, la première variante trouvée, quel que soit le pays.
  if (labels) {
    const vals = Object.values(labels).filter(objet);
    if (vals.length) return vals[0];
  }
  if (i18n) {
    for (const parPays of Object.values(i18n)) {
      const premiere = Object.values(objet(parPays) || {}).find(objet);
      if (premiere) return premiere;
    }
  }
  return null;
}


/**
 * Notices réglementaires du pays demandé, ou la liste générale.
 *
 * `notices` = { "FR": [ … ] } indexé par pays ; `regulatory_notices` est la
 * liste générale, servie quand la fiche n'a pas de variante par pays.
 */
export function resolveNoticesForCountry(source, countryCode) {
  if (!source) return [];
  const parPays = source.notices;
  if (parPays && typeof parPays === 'object' && !Array.isArray(parPays)) {
    if (countryCode && Array.isArray(parPays[countryCode])) return parPays[countryCode];
    const premiere = Object.values(parPays).find(Array.isArray);
    if (premiere) return premiere;
  }
  if (Array.isArray(parPays)) return parPays;
  if (Array.isArray(source.regulatory_notices)) return source.regulatory_notices;
  return [];
}


/**
 * Zones prêtes à imprimer (`print`) du pays demandé.
 *
 * Depuis le 14/10/2026, FDS Studio livre l'étiquette déjà composée, zone par
 * zone : `prints` = { "FR": {…} } (mêmes clés que `labels`), `print` = la
 * variante France, `fds.print` sur une FDS custom. LabelForge ne trie, ne
 * fusionne, ne reformule rien : chaque zone s'imprime telle quelle.
 *
 * Pas de repli sur la variante d'un AUTRE pays que la France : imprimer les
 * zones suisses sur une étiquette belge serait pire que retomber sur `label`.
 *
 * @returns {object|null} les zones, ou null si la fiche n'en porte pas (schéma
 *          d'avant le 14/10) — le builder retombe alors sur `label`.
 */
export function resolvePrintForCountry(source, countryCode) {
  if (!source) return null;
  const objet = (o) => (o && typeof o === 'object' && !Array.isArray(o)) ? o : null;
  // Une zone `print` sans aucune zone connue ({} ou { language }) n'est pas une
  // étiquette composée : la prendre faisait disparaître pictogrammes et mentions
  // (relecture de #336, 19/10). On retombe alors sur `label`.
  const utile = (o) => (objet(o) && ZONES_PRINT.some((k) => o[k] != null) ? o : null);
  const prints = objet(source.prints);
  if (prints && countryCode && utile(prints[countryCode])) return prints[countryCode];
  // `print` est la variante FRANCE. Un pays qui a sa propre variante d'étiquette
  // (labels / labels_i18n) mais pas de zones à lui garde cette variante : sinon
  // une étiquette belge s'imprimait avec les zones françaises (relecture du 19/10).
  const varianteDuPays = countryCode && countryCode !== 'FR'
    && (objet(objet(source.labels)?.[countryCode]) || objet(objet(source.labels_i18n)?.[countryCode]));
  if (varianteDuPays) return null;
  return utile(source.print) || utile(source.fds?.print) || null;
}

/** Les zones que FDS Studio sert dans `print` (brief du 14/10). */
const ZONES_PRINT = ['ingredients', 'pictograms', 'hazard', 'contains', 'precautionary_statements',
  'precaution', 'health_warning', 'statements', 'leaflet'];


/**
 * Normalise un "produit fini" issu du catalog vers la même forme qu'une
 * fiche UFI, pour pouvoir réutiliser buildRegulatoryLayers tel quel.
 *
 * @param {object} produit - objet `produits_finis[i]` du catalog
 * @param {string} clientName - nom du client (pour blockId + refresh)
 * @param {string} gammeName  - nom de la gamme (pour blockId + refresh)
 * @param {string|null} countryCode - pays de la variante d'étiquette à appliquer
 * @returns {object} payload compatible buildRegulatoryLayers
 */
export function produitFiniToPayload(produit, clientName, gammeName, countryCode = null,
                                     langCode = null, clientId = null) {
  const etiquette = resolveLabelForCountry(produit, countryCode, langCode);
  return {
    // L'UFI peut être présent dans le catalog (ajouté côté API FDS) : on le
    // propage pour générer le calque UFI même lors d'un import par client.
    // Fallback null si absent → pas de calque UFI.
    ufi: produit.ufi || null,
    // Référence interne du produit chez le client. PAS de calque : elle n'est
    // pas imprimée sur les étiquettes vues jusqu'ici. Elle est portée dans le
    // `regBlock` pour être identifiable et rapatriable si le besoin vient.
    internal_reference: produit.internal_reference ?? null,
    formulation: {
      name: produit.name,
      pg: produit.pg,
      vg: produit.vg,
      nicotine: produit.nicotine,
      // `nicotine_free` est explicite : un produit sans nicotine n'écrit pas
      // « 0 mg/ml » mais « Sans nicotine ». Sans ce drapeau, les deux cas se
      // confondaient dans un même zéro.
      nicotine_free: produit.nicotine_free ?? null,
      // `nicotine_salt` a TROIS états : vrai, faux, et null = inconnu (aucune
      // recette). Le `!!` d'avant écrasait l'inconnu en faux ; on n'écrit la
      // mention que sur un vrai, et rien sur un null.
      nicotine_salt: produit.nicotine_salt ?? null,
      volume_ml: produit.volume_ml ?? null,
    },
    distributor: produit.distributor ?? null,
    // Zones prêtes à imprimer du pays : quand elles sont là, le builder ne lit
    // plus `label` ni les notices.
    print: resolvePrintForCountry(produit, countryCode),
    // Variante d'étiquette selon le pays ET la langue (schéma `labels_i18n`),
    // fallback sur `labels` puis `label` pour les catalogues précédents.
    label: etiquette,
    regulatory_notices: resolveNoticesForCountry(produit, countryCode),
    _country: countryCode || null,
    _language: langCode || etiquette?.language || null,
    _fetched_at: new Date().toISOString(),
    // Marqueur pour le builder + le banner refresh
    _catalog: {
      client: clientName,
      // Identifiant du client dans le catalogue FDS : c'est lui qui se passe à
      // /catalog/clients/{id}. Absent des blocs importés avant le 16/09/2026.
      client_id: clientId || null,
      gamme: gammeName,
      produit: produit.name,
    },
  };
}


/**
 * Map code GHS → forme correspondante dans la bibliothèque (calcul one-time).
 * Les ids dans le shape library sont 'official-ghs01' à 'official-ghs09' ;
 * on indexe sur le code uppercase ('GHS01') pour matching simple.
 */
const GHS_SHAPE_BY_CODE = (() => {
  const out = {};
  for (const s of SHAPE_LIBRARY) {
    const m = /^official-(ghs\d{2})$/i.exec(s.id || '');
    if (m && s.svgContent) {
      out[m[1].toUpperCase()] = s;
    }
  }
  // La FEMME ENCEINTE que FDS Studio pose dans ses zones `print` (PICTO-PREGNANT) : notre SVG officiel,
  // plutôt que son image distante, qui arrivait avec un carré blanc autour du cercle (Redouane, 24/10 :
  // « on est sûr que c'est du SVG, cette affaire ? »). Le −18 et le Triman, eux, restent ceux de FDS
  // Studio (Redouane, même soir : « on garde le −18 du Studio, on garde le Triman »).
  const parId = Object.fromEntries(SHAPE_LIBRARY.map((s) => [s.id, s]));
  for (const [code, id] of [['PICTO-PREGNANT', 'official-pregnant'], ['PICTO-ENCEINTE', 'official-pregnant']]) {
    if (parId[id]?.svgContent) out[code] = parId[id];
  }
  return out;
})();


/**
 * Résout le SVG officiel local d'un pictogramme à partir de son code GHS.
 * Utilisé pour ré-instancier un pictogramme depuis un modèle .lfgr (qui ne
 * stocke que le code, pas le SVG). Renvoie null si le code n'est pas dans la
 * bibliothèque locale.
 */
export function resolvePictogramShape(code) {
  const s = GHS_SHAPE_BY_CODE[(code || '').toUpperCase()];
  return s ? { svgContent: s.svgContent, svgViewBox: s.viewBox } : null;
}


/** Préfixe lisible du calque dans la liste, ex: "REG · Phrases H". */
const ROLE_LABELS = {
  pictogram:     'REG · Pictogramme',
  signal_word:   'REG · Mot d\'avertissement',
  hazard:        'REG · Phrases H',
  // Phrases P : posées par la zone `precautionary_statements` depuis le 14/10 ;
  // jamais par l'ancien chemin `label` (décision antérieure, inchangée là).
  precaution:    'REG · Phrases P',
  // « Produit interdit aux mineurs. En cas de consultation médicale… » — zone
  // `precaution` de FDS Studio, servie AUSSI pour les produits sans nicotine
  // (relevé du 14/10). Un rôle À PART : `precaution` désigne déjà les phrases P,
  // et le même nom aurait confondu les deux au rafraîchissement d'un ancien bloc.
  precaution_usage: 'REG · Précautions d\'usage',
  health_warning: 'REG · Avertissement sanitaire',
  statements:    'REG · Mentions imposées',
  leaflet:       'REG · Notice nicotine',
  euh:           'REG · Phrases EUH',
  contains:      'REG · Contient (allergènes)',
  supplementary: 'REG · Mentions complémentaires',
  notice:        'REG · Notices',
  composition:   'REG · Composition',
  distributor:   'REG · Distributeur',
  volume:        'REG · Volume',
  nicotine:      'REG · Nicotine',
  nicotine_salt: 'REG · Sels de nicotine',
  pg:            'REG · PG',
  vg:            'REG · VG',
  pg_vg_ratio:   'REG · Ratio PG/VG',  // conservé pour compatibilité refresh ancien blocs
  ufi:           'REG · UFI',
};


/** Couleur orange uniforme pour tous les calques REG (cohérent dans la liste). */
const REG_COLOR = '#F97316';


/**
 * Retire un éventuel préfixe « code » (H315, EUH208…) en tête d'une phrase
 * réglementaire. On ne veut afficher QUE la phrase. L'API renvoie désormais un
 * texte propre, mais ce garde-fou rend l'import robuste si une fiche renvoie
 * encore le code collé au texte (ancien format). Sans préfixe → no-op.
 */
function stripStatementCode(text, code) {
  if (!text) return '';
  let t = String(text).trim();
  // 1) Retire le code EXACT fourni (premier token de stmt.code), ex "EUH208".
  if (code) {
    const token = String(code).trim().split(/\s+/)[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (token) t = t.replace(new RegExp(`^${token}\\s*[-–:.]?\\s*`, 'i'), '');
  }
  // 2) Garde-fou générique : tout code CLP (Hxxx / EUHxxx) resté en tête.
  t = t.replace(/^(EUH\d{3}[A-Za-z]*|H\d{3}[A-Za-z]*)\s*[-–:.]?\s+/i, '');
  return t.trim();
}


/**
 * Compose le bloc "Contient : ..." à partir de la liste d'allergènes.
 * Texte unique sur une (ou plusieurs) ligne(s), séparateur virgule.
 */
function formatContains(list, prefixe) {
  if (!list || list.length === 0) return null;
  // Le préfixe vient de la fiche (`label.contains_label`) : sur une variante
  // étrangère, « Contient » doit devenir « Contains » ou « Enthält ». Il était
  // écrit en dur, ce qui rendait toute variante non française à moitié traduite.
  return `${(prefixe || 'Contient').trim()} : ${list.join(', ')}.`;
}


/** Texte d'un élément dont le schéma abrège la forme : chaîne, ou objet dont on
 *  ne sait pas laquelle de ces clés porte le libellé. Se tromper de clé
 *  donnerait une mention VIDE sur l'étiquette — pire qu'une mention absente. */
function texteSouple(x) {
  if (x === null || x === undefined) return '';
  if (typeof x === 'string' || typeof x === 'number') return String(x).trim();
  if (typeof x !== 'object') return '';
  const v = x.text ?? x.texte ?? x.label ?? x.libelle ?? x.name ?? x.nom
         ?? x.title ?? x.titre ?? x.value ?? x.valeur ?? '';
  return String(v).trim();
}


/**
 * Texte d'une zone `print`, tel quel.
 *
 * Forme relevée sur le catalogue de production le 14/10 : `ingredients` est
 * une chaîne, les autres zones de texte des LISTES de chaînes (`hazard` =
 * ["Danger", "Toxique en cas d'ingestion."]), une entrée par ligne. Un objet
 * donne son libellé. Rien n'est trié, fusionné ni reformulé : c'est tout
 * l'objet du champ.
 */
function texteZone(z) {
  if (Array.isArray(z)) return z.map(texteSouple).filter(Boolean).join('\n');
  return texteSouple(z);
}


/**
 * Notice nicotine (zone `leaflet`) : une liste de rubriques `{ title, lines }`,
 * ou `null` pour un produit sans nicotine.
 *
 * Mise en page de la notice remise par l'atelier (13/10/2026) : le titre de la
 * rubrique seul sur sa ligne, puis ses phrases à puces. Pas de ligne vide entre
 * deux rubriques : le texte est long, et la hauteur manque sur un 10 ml. Aucun
 * mot n'est changé — seules les puces et les retours à la ligne sont à nous.
 * Une rubrique sans titre garde ses phrases ; une rubrique vide disparaît.
 *
 * @param {Array<{title?:string, lines?:string[]}>|null} leaflet
 * @returns {string|null} le texte du calque, ou null s'il n'y a rien à poser
 */
function formatLeaflet(leaflet) {
  // Une seule rubrique servie hors liste, ou une notice en une chaîne : tolérées
  // (relecture de #336 : elles étaient écartées sans un mot).
  if (typeof leaflet === 'string' || (leaflet && typeof leaflet === 'object' && !Array.isArray(leaflet))) leaflet = [leaflet];
  if (!Array.isArray(leaflet) || leaflet.length === 0) return null;
  const rubriques = leaflet.map((r) => {
    // Une rubrique réduite à une chaîne est une phrase sans titre.
    const titre = typeof r === 'string' ? '' : texteSouple(r?.title);
    const brut = typeof r === 'string' ? [r] : (Array.isArray(r?.lines) ? r.lines : []);
    const phrases = brut.map(texteSouple).filter(Boolean).map((p) => `• ${p}`);
    return [titre, ...phrases].filter(Boolean).join('\n');
  }).filter(Boolean);
  return rubriques.length ? rubriques.join('\n') : null;
}


/**
 * Distributeur : le nom puis son adresse, une ligne par ligne.
 *
 * `{ name, lines: [...] }` dans le schéma. On tolère `address`/`adresse` à la
 * place de `lines`, et la chaîne nue, parce que ce champ est de ceux qu'un
 * fournisseur de données remanie sans prévenir.
 */
export function formatDistributor(d) {
  if (!d) return null;
  if (typeof d === 'string') return d.trim() || null;
  const nom = texteSouple(d);
  const brut = d.lines ?? d.address ?? d.adresse ?? d.lignes ?? [];
  const lignes = (Array.isArray(brut) ? brut : [brut]).map(texteSouple).filter(Boolean);
  const tout = [nom, ...lignes].filter(Boolean);
  return tout.length ? tout.join('\n') : null;
}


/** Volume : « 10 ml ». `volume_ml` dans le schéma, `volume` toléré. */
function formatVolume(formulation) {
  if (!formulation) return null;
  const v = formulation.volume_ml ?? formulation.volume;
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? `${n} ml` : `${String(v).trim()}`;
}


/**
 * Notices réglementaires : une par ligne, dans UN calque groupé.
 *
 * Deux entrées ne sont PAS du texte à imprimer, et le schéma les distingue :
 *   - `requirement: true` — une CONSIGNE au maquettiste (« Les contre-indications »
 *     = rubrique à rédiger dans la notice), pas une phrase. La notice française
 *     n'a aucun texte officiel : l'imprimer telle quelle écrivait le nom de la
 *     rubrique sur l'étiquette.
 *   - `kind: "pictogram"` — un pictogramme à POSER, dont le fichier officiel
 *     n'est pas toujours fourni (`image_url` absent). Son libellé n'est pas une
 *     mention.
 * Une chaîne nue (ancien schéma) passe telle quelle.
 */
function formatNotices(liste) {
  if (!Array.isArray(liste) || liste.length === 0) return null;
  const imprimable = (n) => !(n && typeof n === 'object' && (n.requirement === true || n.kind === 'pictogram'));
  const lignes = liste.filter(imprimable).map(texteSouple).filter(Boolean);
  return lignes.length ? lignes.join('\n') : null;
}


/**
 * Compose le texte de la mention nicotine.
 *
 * Format demandé : juste "X mg/ml" (sans préfixe "Nicotine :", sans suffixe
 * "(sels de nicotine)"). Pour 0 mg → "0 mg/ml" (info quand même utile sur
 * l'étiquette d'un e-liquide).
 *
 * Lit `formulation.nicotine` (nombre direct, format API courant), fallback
 * sur `formulation.nicotine_mg` (ancien format).
 */
function formatNicotine(formulation) {
  if (!formulation) return null;
  // `nicotine_free` est explicite et prime : « Sans nicotine » n'est pas la même
  // mention que « 0 mg/ml ». Un produit à 0 mg SANS ce drapeau garde son
  // « 0 mg/ml » — l'information reste utile sur un e-liquide.
  if (formulation.nicotine_free === true) return 'Sans nicotine';
  const mg = formulation.nicotine !== undefined && formulation.nicotine !== null
    ? formulation.nicotine
    : formulation.nicotine_mg;
  if (mg === null || mg === undefined) return null;
  return `${mg} mg/ml`;
}


/**
 * Récupère les valeurs PG et VG séparées.
 *
 * Nouveau format API : `formulation.pg` + `formulation.vg` (deux nombres).
 * Ancien format : `formulation.vg_pg_ratio` = "50/50" ou { pg, vg } — décodé
 * en fallback pour les fiches importées avant la migration API.
 *
 * @returns {{ pg: number|null, vg: number|null }}
 */
function extractPgVg(formulation) {
  if (!formulation) return { pg: null, vg: null };

  // Nouveau format : champs séparés
  if (
    formulation.pg !== undefined && formulation.pg !== null &&
    formulation.vg !== undefined && formulation.vg !== null
  ) {
    return { pg: formulation.pg, vg: formulation.vg };
  }

  // Ancien format : vg_pg_ratio
  const r = formulation.vg_pg_ratio;
  if (r === null || r === undefined) return { pg: null, vg: null };
  if (typeof r === 'object' && r.pg !== undefined && r.vg !== undefined) {
    return { pg: r.pg, vg: r.vg };
  }
  if (typeof r === 'string' && r.includes('/')) {
    const [pgStr, vgStr] = r.split('/');
    const pg = parseInt(pgStr.trim(), 10);
    const vg = parseInt(vgStr.trim(), 10);
    if (!Number.isNaN(pg) && !Number.isNaN(vg)) return { pg, vg };
  }
  return { pg: null, vg: null };
}


/**
 * Construit la metadata partagée par tous les calques d'un même bloc.
 * Distingue les deux origines possibles :
 *   - Import par UFI       → blockId dérivé de l'UFI
 *   - Import par catalog   → blockId dérivé de client+gamme+produit,
 *                            metadata.catalog présente pour refresh
 */
function buildRegBlockMeta(payload, role) {
  const isCatalog = !!payload._catalog;
  const blockId = isCatalog
    ? blockIdFromCatalog(payload._catalog.client, payload._catalog.gamme, payload._catalog.produit)
    : blockIdFromUfi(payload.ufi);
  return {
    blockId,
    role,
    ufi: payload.ufi || null,
    catalog: isCatalog ? { ...payload._catalog } : null,
    source: isCatalog ? 'catalog' : 'ufi',
    country: payload._country || null,   // pays de la variante (rejoué au refresh)
    // Langue de la variante : le schéma sert désormais `labels_i18n[pays][langue]`.
    // Rangée ici pour que le rafraîchissement rejoue la MÊME variante, et pas la
    // première que la fiche propose.
    language: payload._language || payload.label?.language || null,
    // Référence interne du produit. Aucune mention ne l'imprime aujourd'hui :
    // elle sert à identifier le produit, pas à figurer sur l'étiquette.
    internal_reference: payload.internal_reference ?? null,
    fetched_at: payload._fetched_at || new Date().toISOString(),
  };
}


/**
 * Dimensions utiles d'un canevas, en mm.
 *
 * Le canevas du store porte `width_mm` / `height_mm`. Lire `canvas.width` — ce
 * que faisait cette fonction — renvoyait donc TOUJOURS `undefined`, et le
 * placeur retombait sur son défaut de 100 × 100 mm : quelle que soit
 * l'étiquette, les mentions partaient à `x = 60` et s'empilaient jusqu'à ~51 mm
 * de haut. Sur une 131 × 34, « PG » et « VG » atterrissaient à y = 40,5 et
 * y = 46 — hors du plan de travail, donc coupés à l'export, sans un mot.
 * *Constaté sur le retour utilisateur du 2026-08-28.*
 *
 * On accepte les deux formes : `width_mm` (le store, l'export, les projets) et
 * `width` (appelants historiques), pour qu'un appelant oublié ne retombe pas en
 * silence sur le carré de 100 mm.
 */
function dimensionsCanvas(canvas) {
  const w = canvas?.width_mm ?? canvas?.width;
  const h = canvas?.height_mm ?? canvas?.height;
  return {
    width: Number.isFinite(w) && w > 0 ? w : 100,
    height: Number.isFinite(h) && h > 0 ? h : 100,
  };
}


/**
 * Position initiale des calques : une colonne le long du bord droit, remplie de
 * haut en bas, les pictogrammes dans une bande au-dessus. C'est un point de
 * départ que l'utilisateur déplacera — pas une mise en page.
 *
 * La seule garantie que cette fonction doit tenir : **rien ne sort du plan de
 * travail.** Une mention légale posée dehors est invisible dans l'atelier ET
 * coupée à l'export : c'est le silence le plus coûteux de la chaîne. Quand la
 * colonne est pleine, on repart en haut avec un léger décalage — deux mentions
 * qui se chevauchent, ça se voit et ça se corrige ; une mention hors plan, non.
 *
 * Un cadre plus large que l'étiquette est rétréci, et un cadre placé trop à
 * droite est ramené dans le plan : c'est ce contrôle-là qui manquait, et il
 * laissait « PG » et « VG » sous le bord d'une étiquette de 34 mm de haut.
 */
function makePlacer(canvasWidth, canvasHeight) {
  // Marge proportionnée : 5 mm sur une étiquette normale, moins sur une petite.
  // Un bord fixe de 5 mm sur une 12 × 8 ne laissait que 2 mm exploitables, et le
  // plancher de largeur reposait alors le cadre hors du plan.
  const margin = Math.max(0.5, Math.min(5, canvasWidth / 8, canvasHeight / 8));
  const utileW = Math.max(1, canvasWidth - 2 * margin);
  const bas = Math.max(margin + 1, canvasHeight - margin);    // dernière ordonnée utile
  const utileH = bas - margin;

  let y = margin;
  let tour = 0;              // nombre de fois qu'on est revenu en haut
  let bandePicto = 0;        // hauteur de la rangée de pictogrammes, à consommer
  let hautTexte = margin;    // première ordonnée libre pour un texte
  let pictoIndex = 0;
  // Pas du décalage quand on revient en haut : assez large pour qu'on distingue
  // et qu'on attrape chaque mention, jamais plus que ce que l'étiquette offre.
  const pasDecalage = Math.min(10, Math.max(2, utileW / 10));

  // Positions déjà servies : sur une étiquette où tout ne tient pas, le calage
  // dans le plan peut ramener deux cadres au même point — et deux mentions
  // exactement superposées sont inattrapables à la souris.
  const servies = new Set();

  /** Ramène un cadre (w × h) dans le plan de travail, à l'abscisse souhaitée,
   *  puis le décale d'un cheveu s'il tombe pile sur une position déjà servie. */
  const caler = (xVoulu, w, h) => {
    const xMax = Math.max(margin, canvasWidth - margin - w);
    const yMax = Math.max(margin, bas - h);
    let x = Math.min(Math.max(margin, xVoulu), xMax);
    let cy = Math.min(Math.max(margin, y), yMax);
    for (let n = 0; n < 40 && servies.has(`${x.toFixed(2)}/${cy.toFixed(2)}`); n += 1) {
      // On glisse en diagonale, puis on repart du coin si on butte sur le bord.
      x = x + 0.8 <= xMax ? x + 0.8 : margin;
      cy = cy + 0.8 <= yMax ? cy + 0.8 : margin;
    }
    servies.add(`${x.toFixed(2)}/${cy.toFixed(2)}`);
    return { x, y: cy, width: w, height: h };
  };

  return {
    nextPicto(size) {
      // Le pictogramme est la SEULE chose que ce placeur ne rétrécit pas sous sa
      // taille demandée. La garantie « rien ne sort du plan » s'arrête ici, et
      // c'est volontaire : un losange GHS sous 16 mm n'est plus conforme, et
      // troquer « dépasse du plan » (visible, et signalé ailleurs) contre « trop
      // petit pour la loi » (muet, et imprimé tel quel) serait un mauvais
      // échange. Sur une étiquette qui ne peut pas contenir 16 mm, aucune mise en
      // page n'est conforme : autant que la vraie emprise se voie.
      const t = Math.max(PICTO_MIN_MM, Math.min(size, utileW, utileH));
      // Alignés sur le bord droit, le suivant vers la gauche : la zone REG est
      // à droite, et un picto posé à gauche tomberait au milieu du visuel.
      const pos = caler(canvasWidth - margin - t - pictoIndex * (t + 2), t, t);
      pictoIndex += 1;
      bandePicto = Math.max(bandePicto, t);
      return pos;
    },
    largeurUtile() { return utileW; },
    nextText(width, height) {
      const w = Math.min(width, utileW);
      const h = Math.min(height, utileH);
      // Première mention après les pictogrammes : on descend sous leur bande,
      // sinon le texte se pose sur le losange. `y` ne bougeait pas du tout au
      // passage d'un picto, alors que le commentaire annonçait « après les
      // pictos ».
      if (bandePicto) {
        const sousLesPictos = margin + bandePicto + 1.5;
        // Sur une étiquette basse, la bande des pictogrammes mange presque toute
        // la hauteur : commencer en dessous ne laisserait pas de place. On garde
        // alors le haut du plan et on assume le chevauchement, qui se voit.
        hautTexte = (bas - sousLesPictos >= 6) ? sousLesPictos : margin;
        y = hautTexte;
        bandePicto = 0;
      }
      // Colonne pleine : on repart en haut de la zone de texte — SOUS les
      // pictogrammes, sinon la mention suivante se pose sur le losange —, décalée
      // vers la gauche, plutôt que de sortir du plan.
      if (y + h > bas) { tour += 1; y = hautTexte; }
      // Le décalage se fait vers la GAUCHE : vers la droite, il était annulé net
      // par le calage dans le plan, et les mentions s'empilaient au pixel près.
      const pos = caler(canvasWidth - margin - w - tour * pasDecalage, w, h);
      y = pos.y + h + 1.5;
      return pos;
    },
  };
}


/**
 * Génère les calques à partir d'une fiche FDS Polyprint.
 *
 * @param {object} payload - le JSON renvoyé par GET /api/regulatory/{ufi}
 * @param {object} canvas  - { width, height } en mm (pour positionner)
 * @returns {Array<object>} liste de calques prêts pour `addElement(...)`
 */
export function buildRegulatoryLayers(payload, canvas) {
  if (!payload) {
    throw new Error('Fiche réglementaire vide.');
  }
  // `label` peut être null (produit « Sans recette » / sans étiquetage) : on ne
  // jette plus — on génère ce qu'on peut (formulation, UFI) et on saute
  // simplement les sections d'étiquette absentes. Caller : si 0 calque, prévenir.
  //
  // Quand la fiche porte ses zones prêtes à imprimer (`print`, depuis le
  // 14/10/2026), `label` et les notices ne sont PLUS lus : un `lbl` vide éteint
  // les sections historiques, et la section 1b pose les zones à leur place.
  const zones = resolvePrintForCountry(payload, payload._country);
  const lbl = zones ? {} : (payload.label || {});
  // Données de formulation : le nouveau schéma met nicotine/pg/vg À PLAT sur le
  // produit. On lit `payload.formulation` s'il existe (import catalog normalisé
  // via produitFiniToPayload), sinon `payload` lui-même (réponse per-UFI).
  const form = payload.formulation || payload;
  const { width: canvasW, height: canvasH } = dimensionsCanvas(canvas);
  const placer = makePlacer(canvasW, canvasH);
  const layers = [];

  // ─── 1. Pictogrammes ───
  // On préfère le SVG officiel embarqué (vectoriel, hors-ligne, contrôle
  // total) au PNG distant fourni par l'API. Si le code GHS n'est pas dans
  // la bibliothèque locale, on retombe gracieusement sur l'image URL.
  // Avec des zones, `pictograms` liste les GHS PUIS les pictogrammes nationaux
  // (interdit aux mineurs, Triman…), chacun avec son `image_url`.
  const pictoSize = PICTO_MIN_MM; // mm — taille mini CLP, conditionnement ≤ 3 L
  const pictos = zones ? zones.pictograms : lbl.pictograms;
  for (const p of (Array.isArray(pictos) ? pictos : [])) {
    const code = (p?.code || '').toUpperCase();
    const localShape = GHS_SHAPE_BY_CODE[code];
    // Ni SVG local ni fichier fourni : un calque image sans source s'imprimerait
    // vide. Mieux vaut l'absence, que le compte rendu de l'import signale.
    if (!localShape && !p?.image_url) continue;
    const pos = placer.nextPicto(pictoSize);

    if (localShape) {
      // ─── Variante SVG local (calque type 'shape') ───
      layers.push({
        type: 'shape',
        shapeType: 'svg',
        svgContent: localShape.svgContent,
        svgViewBox: localShape.viewBox,
        svgFill: true,
        name: `${ROLE_LABELS.pictogram} ${p.code}`,
        x: pos.x, y: pos.y, width: pos.width, height: pos.height,
        rotation: 0,
        regBlock: { ...buildRegBlockMeta(payload, 'pictogram'), pictogramCode: p.code },
        contentLocked: true,
      });
    } else {
      // ─── Fallback : image distante (calque type 'image') ───
      layers.push({
        type: 'image',
        url: p.image_url,
        name: `${ROLE_LABELS.pictogram} ${p.code}`,
        x: pos.x, y: pos.y, width: pos.width, height: pos.height,
        rotation: 0,
        regBlock: { ...buildRegBlockMeta(payload, 'pictogram'), pictogramCode: p.code },
        contentLocked: true,
      });
    }
  }

  // ─── 1b. Zones prêtes à imprimer (`print`) ───
  // Une zone = un calque, son texte tel que FDS Studio l'a composé. Les rôles
  // déjà connus des modèles .lfgr sont repris quand le sens est le même, pour
  // que les gabarits existants placent la zone sans être refaits :
  //   ingredients → composition · hazard → hazard (mot d'avertissement compris)
  //   contains → contains (EUH comprises) · precautionary_statements → precaution
  // Les anciens calques `signal_word`, `euh`, `supplementary` et `notice` ne sont
  // plus produits : le rafraîchissement d'un ancien bloc les retire (staleIds).
  if (zones) {
    const poser = (role, texte, nom = ROLE_LABELS[role], consignes = null) => {
      if (!texte) return;
      // Hauteur du cadre : les lignes APRÈS retour automatique, pas les seuls
      // « \n ». Le rendu d'export COUPE ce qui dépasse du cadre : compter les
      // seuls « \n » donnait 5 mm à une phrase de 180 caractères, et la dernière
      // phrase P comme la fin de la notice s'imprimaient tronquées, sans un mot
      // (rendu du 14/10 sur les zones réelles). L'étalon (hauteurTexte.js) suit le
      // corps ET la police : le 3e et le 4e argument sont ceux du calque posé,
      // sinon un cadre compté en Arial 7 pt pour un texte en Arial Narrow 5,5 pt
      // serait 1,6 fois trop haut. Trop haut se voit et se réduit ; trop bas
      // s'imprime coupé.
      // La hauteur se calcule sur la largeur que le placeur donnera VRAIMENT : sur
      // une petite étiquette il rétrécit le cadre, et une hauteur comptée pour
      // 60 mm coupait la fin (relecture de #336 : 30 mm de large, 14 mm posés
      // pour 17,8 nécessaires).
      const largeur = Math.min(60, placer.largeurUtile());
      const pos = placer.nextText(largeur, hauteurTexteEstimee(texte, largeur, CORPS_MENTIONS_PT, POLICE_MENTIONS));
      layers.push({
        type: 'text',
        text: texte,
        name: nom,
        x: pos.x, y: pos.y, width: pos.width, height: pos.height,
        font: POLICE_MENTIONS, size: CORPS_MENTIONS_PT, color: '#000000',
        textAlign: 'left', lineHeight: 1.2,
        regBlock: { ...buildRegBlockMeta(payload, role), ...consignes },
        contentLocked: true,
      });
    };
    poser('hazard', texteZone(zones.hazard), 'REG · Danger (mention + phrases H)');
    poser('contains', texteZone(zones.contains), 'REG · Contient et EUH');
    poser('precaution', texteZone(zones.precautionary_statements));
    poser('precaution_usage', texteZone(zones.precaution));
    // `health_warning` est une LISTE d'avertissements — forme relevée sur le
    // catalogue de production le 14/10 : [{ text, occurrences: 2, coverage_pct: 30,
    // placement, language }], vide pour un produit sans nicotine. Un objet seul
    // est toléré. Chaque avertissement s'imprime `occurrences` fois ; tous les
    // exemplaires portent le même rôle : au rafraîchissement, tous reçoivent le
    // texte à jour. Plafonné à 4 : une valeur aberrante ne doit pas noyer
    // l'étiquette. Les consignes de la fiche restent sur chaque exemplaire,
    // brutes : le contrôle de l'étiquette juge « 2 fois, sur 30 % » sans les
    // écrire en dur.
    const hws = zones.health_warning;
    // Chaque exemplaire porte son identité (`exemplaire` : « avertissement.copie »),
    // reprise dans la clé d'appariement : avec le seul rôle, la réconciliation
    // donnait à tous les exemplaires le texte du DERNIER avertissement, ne suivait
    // pas un changement du nombre d'exemplaires, et la disposition de l'agent les
    // empilait au même endroit (relecture de #336, 19/10).
    const liste = Array.isArray(hws) ? hws : (hws ? [hws] : []);
    for (const [a, hw] of liste.entries()) {
      const n = Number(hw?.occurrences);
      const exemplaires = Number.isInteger(n) && n > 0 ? Math.min(n, 4) : 1;
      const consignes = {
        occurrences: hw?.occurrences ?? null,
        coverage_pct: hw?.coverage_pct ?? null,
        placement: hw?.placement ?? null,
      };
      for (let i = 0; i < exemplaires; i += 1) {
        poser('health_warning', texteSouple(hw), ROLE_LABELS.health_warning, { ...consignes, exemplaire: `${a + 1}.${i + 1}` });
      }
    }
    poser('statements', texteZone(zones.statements));
    poser('composition', texteZone(zones.ingredients));
    poser('leaflet', formatLeaflet(zones.leaflet));
  }

  // ─── 2. Mot d'avertissement (texte court bold) ───
  if (lbl.signal_word?.text) {
    const pos = placer.nextText(35, 5);
    layers.push({
      type: 'text',
      text: lbl.signal_word.text,
      name: ROLE_LABELS.signal_word,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: 'Arial', size: 12, color: '#000000',
      fontWeight: 'bold', textAlign: 'left',
      regBlock: buildRegBlockMeta(payload, 'signal_word'),
      contentLocked: true,
    });
  }

  // ─── 3. Phrases H (hazard statements) — regroupées en UN calque ───
  // Affichage : uniquement le TEXTE de chaque phrase (sans le code Hxxx), une
  // phrase par ligne. UN SEUL calque groupé (pas un calque par phrase, qui
  // éparpillerait l'étiquette) — déplaçable d'un bloc.
  if (lbl.hazard_statements?.length) {
    const lines = lbl.hazard_statements.map(s => stripStatementCode(s.text, s.code)).filter(Boolean).join('\n');
    const linesCount = lbl.hazard_statements.length;
    const pos = placer.nextText(60, Math.max(8, linesCount * 3.5));
    layers.push({
      type: 'text',
      text: lines,
      name: ROLE_LABELS.hazard,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: POLICE_MENTIONS, size: CORPS_MENTIONS_PT, color: '#000000',
      textAlign: 'left', lineHeight: 1.2,
      regBlock: buildRegBlockMeta(payload, 'hazard'),
      contentLocked: true,
    });
  }

  // ─── 4. Phrases P (precautionary statements) ───
  // Ancien chemin (fiche sans `print`) : toujours pas posées, décision
  // antérieure. Avec des zones, `precautionary_statements` les pose (section 1b) :
  // le 14/10/2026, on a choisi de suivre FDS Studio, qui porte le contenu.

  // ─── 5. Phrases EUH — regroupées en UN calque (idem phrases H) ───
  if (lbl.euh_statements?.length) {
    const lines = lbl.euh_statements.map(s => stripStatementCode(s.text, s.code)).filter(Boolean).join('\n');
    const linesCount = lbl.euh_statements.length;
    const pos = placer.nextText(60, Math.max(5, linesCount * 3.5));
    layers.push({
      type: 'text',
      text: lines,
      name: ROLE_LABELS.euh,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: POLICE_MENTIONS, size: CORPS_MENTIONS_PT, color: '#000000',
      textAlign: 'left', lineHeight: 1.2,
      regBlock: buildRegBlockMeta(payload, 'euh'),
      contentLocked: true,
    });
  }

  // ─── 6. Allergènes (Contient : ...) ───
  const containsText = formatContains(lbl.contains, lbl.contains_label);
  if (containsText) {
    const pos = placer.nextText(60, 6);
    layers.push({
      type: 'text',
      text: containsText,
      name: ROLE_LABELS.contains,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: POLICE_MENTIONS, size: CORPS_MENTIONS_PT, color: '#000000',
      fontStyle: 'italic', textAlign: 'left', lineHeight: 1.2,
      regBlock: buildRegBlockMeta(payload, 'contains'),
      contentLocked: true,
    });
  }

  // ─── 6b. Mentions complémentaires (supplementary) ───
  // Ex : « Produit contenant de la nicotine. Substance qui crée une forte
  // dépendance. » — souvent obligatoire pour les e-liquides. Liste de phrases
  // → UN calque groupé (une phrase par ligne), comme les phrases H/EUH.
  //
  // Depuis le 16/09/2026 le schéma sépare ce qui s'IMPRIME (`supplementary_statements`
  // : EUH208, EUH210, texte repris d'une FDS) de ce qui est une contrainte de
  // CONDITIONNEMENT (`packaging_requirements` : fermeture de sécurité pour enfants,
  // avertissement tactile). `supplementary` est le contrat historique qui mélange
  // les deux — il n'est lu qu'en secours, quand la fiche n'a pas encore la
  // séparation. Un `supplementary_statements` PRÉSENT mais vide veut dire « rien
  // à imprimer », pas « retombe sur l'ancien champ ». Et `packaging_requirements`
  // n'est jamais lu ici : « Récipient muni d'une fermeture de sécurité pour
  // enfants » n'est pas une mention d'étiquette.
  const supplementaires = Array.isArray(lbl.supplementary_statements)
    ? lbl.supplementary_statements
    : (Array.isArray(lbl.supplementary) ? lbl.supplementary : []);
  if (supplementaires.length) {
    const lines = supplementaires.map(texteSouple).filter(Boolean).join('\n');
    if (lines) {
      const pos = placer.nextText(60, Math.max(6, supplementaires.length * 3.5));
      layers.push({
        type: 'text',
        text: lines,
        name: ROLE_LABELS.supplementary,
        x: pos.x, y: pos.y, width: pos.width, height: pos.height,
        font: POLICE_MENTIONS, size: CORPS_MENTIONS_PT, color: '#000000',
        textAlign: 'left', lineHeight: 1.2,
        regBlock: buildRegBlockMeta(payload, 'supplementary'),
        contentLocked: true,
      });
    }
  }

  // ─── 6c. Notices réglementaires ───
  // `regulatory_notices`, ou `notices[pays]` quand la fiche décline par pays.
  // Regroupées en UN calque, une notice par ligne, comme les phrases H/EUH :
  // un calque par notice éparpillerait l'étiquette.
  const noticesText = zones ? null : formatNotices(payload.regulatory_notices?.length
    ? payload.regulatory_notices
    : resolveNoticesForCountry(payload, payload._country));
  if (noticesText) {
    const n = noticesText.split('\n').length;
    const pos = placer.nextText(60, Math.max(6, n * 3.5));
    layers.push({
      type: 'text',
      text: noticesText,
      name: ROLE_LABELS.notice,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: POLICE_MENTIONS, size: CORPS_MENTIONS_PT, color: '#000000',
      textAlign: 'left', lineHeight: 1.2,
      regBlock: buildRegBlockMeta(payload, 'notice'),
      contentLocked: true,
    });
  }

  // ─── 6d. Composition ───
  // Plus jamais lue dans la fiche : `composition` y est la section 3 de la FDS,
  // qui n'est PAS la liste d'ingrédients de l'étiquette — l'atelier y trouvait
  // « Éthanol pur, Vanilline (Poudre)… » et les recopiait en double avec le
  // « Contient » (compte rendu du 13/10/2026). Elle vient de la zone
  // `ingredients` (section 1b), et seulement d'elle.

  // ─── 6e. Distributeur ───
  // Nom puis adresse, une ligne par ligne. Mention obligatoire, et jusqu'ici
  // recopiée de mémoire d'une étiquette à l'autre. Dans le corps des autres
  // mentions (Arial Narrow 5,5 pt, interligne 1,2) : 1,02 mm de hauteur d'x, au-dessus
  // du 0,9 mm que l'Omnibus VI demande jusqu'à 125 ml (règle HAUTEUR_X_CLP de
  // controler ; décision du 24/10/2026, CORPS_MENTIONS_PT). Avant, 7 pt (1,30 mm)
  // pour le 1,2 mm du règlement 2025/2439 (#324, #328).
  const distributeurText = formatDistributor(payload.distributor);
  if (distributeurText) {
    const n = distributeurText.split('\n').length;
    const pos = placer.nextText(55, Math.max(5, n * 3.5));
    layers.push({
      type: 'text',
      text: distributeurText,
      name: ROLE_LABELS.distributor,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: POLICE_MENTIONS, size: CORPS_MENTIONS_PT, color: '#000000',
      textAlign: 'left', lineHeight: 1.2,
      regBlock: buildRegBlockMeta(payload, 'distributor'),
      contentLocked: true,
    });
  }

  // ─── 6f. Volume ───
  const volumeText = formatVolume(form);
  if (volumeText) {
    const pos = placer.nextText(18, 4);
    layers.push({
      type: 'text',
      text: volumeText,
      name: ROLE_LABELS.volume,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: 'Arial', size: 8, color: '#000000',
      fontWeight: 'bold', textAlign: 'left',
      regBlock: buildRegBlockMeta(payload, 'volume'),
      contentLocked: true,
    });
  }

  // ─── 7. Nicotine ───
  // Format : juste "X mg/ml" (le nombre + l'unité, rien d'autre). Skip si
  // la valeur n'est pas définie côté API.
  const nicotineText = formatNicotine(form);
  if (nicotineText) {
    const pos = placer.nextText(25, 4);
    layers.push({
      type: 'text',
      text: nicotineText,
      name: ROLE_LABELS.nicotine,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: 'Arial', size: 8, color: '#000000',
      textAlign: 'left',
      regBlock: buildRegBlockMeta(payload, 'nicotine'),
      contentLocked: true,
    });
  }

  // ─── 7b. Sels de nicotine ───
  // `nicotine_salt` a trois états : vrai dès qu'UNE formulation de la recette est
  // en sel, faux, et null = inconnu (aucune recette). On n'écrit la mention QUE
  // sur un vrai — un null n'est pas un faux, et un faux n'a rien à dire. La
  // mention nicotine, elle, reste « X mg/ml » : le suffixe « (sels de nicotine) »
  // y avait été refusé ; ce calque séparé rend la mention sans rouvrir la décision.
  // Jamais sur un produit SANS nicotine : FDS met `nicotine_salt` à vrai dès qu'une
  // formulation de la RECETTE est en sel — le 10 ml nicotiné —, et le shortfill 50 ml
  // de la même recette portait « Sels de nicotine » à côté de « Sans nicotine »
  // (premier essai de l'agent en production, 15/10/2026).
  // Le dosage peut arriver en texte (« 0,0 », « 0 mg/ml ») : la virgule et l'unité ne doivent
  // pas en faire un NaN, qui laissait la mention sur un sans-nicotine (relecture de #343). Une
  // chaîne vide vaut « inconnu », comme null.
  const brut = form.nicotine ?? form.nicotine_mg;
  const dosage = typeof brut === 'string' ? (brut.trim() ? parseFloat(brut.trim().replace(',', '.')) : null) : brut;
  const sansNicotine = form.nicotine_free === true || (dosage !== null && dosage !== undefined && Number(dosage) === 0);
  if (form.nicotine_salt === true && !sansNicotine) {
    const pos = placer.nextText(30, 4);
    layers.push({
      type: 'text',
      text: 'Sels de nicotine',
      name: ROLE_LABELS.nicotine_salt,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: 'Arial', size: 8, color: '#000000',
      textAlign: 'left',
      regBlock: buildRegBlockMeta(payload, 'nicotine_salt'),
      contentLocked: true,
    });
  }

  // ─── 8. PG et VG (deux calques séparés) ───
  // Format demandé : "PG : X" et "VG : X" sur deux calques distincts,
  // pour permettre un placement libre indépendant sur l'étiquette.
  const { pg, vg } = extractPgVg(form);
  if (pg !== null) {
    const pos = placer.nextText(20, 4);
    layers.push({
      type: 'text',
      text: `PG : ${pg}`,
      name: ROLE_LABELS.pg,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: 'Arial', size: 8, color: '#000000',
      textAlign: 'left',
      regBlock: buildRegBlockMeta(payload, 'pg'),
      contentLocked: true,
    });
  }
  if (vg !== null) {
    const pos = placer.nextText(20, 4);
    layers.push({
      type: 'text',
      text: `VG : ${vg}`,
      name: ROLE_LABELS.vg,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: 'Arial', size: 8, color: '#000000',
      textAlign: 'left',
      regBlock: buildRegBlockMeta(payload, 'vg'),
      contentLocked: true,
    });
  }

  // ─── 9. UFI ───
  // Affiché dès qu'un UFI est disponible — qu'il vienne d'un import par UFI
  // OU d'un import par client/catalog (l'API catalog renvoie désormais l'UFI,
  // propagé via produitFiniToPayload). Skip si aucun UFI (anciens catalogues).
  if (payload.ufi) {
    const pos = placer.nextText(35, 4);
    layers.push({
      type: 'text',
      text: `UFI : ${payload.ufi}`,
      name: ROLE_LABELS.ufi,
      x: pos.x, y: pos.y, width: pos.width, height: pos.height,
      font: 'Courier New', size: 8, color: '#000000',
      fontWeight: 'bold', textAlign: 'left',
      regBlock: buildRegBlockMeta(payload, 'ufi'),
      contentLocked: true,
    });
  }

  // Chaque calque dit si le lot vient des zones (`zones`) : un rafraîchissement
  // SANS zones ne retire pas les mentions que seules les zones posent
  // (`gardeFauteDeZones`). Et chaque calque d'un bloc nicotiné porte le nombre
  // d'avertissements sanitaires attendus : `controler` voit ainsi un avertissement
  // supprimé EN ENTIER, qui ne laisse aucun exemplaire pour le dire (relecture du 19/10).
  const avertissementsAttendus = layers.filter((l) => l.regBlock?.role === 'health_warning').length;
  // Couleur orange uniforme dans la liste pour identifier visuellement les
  // calques REG. Note : addElement écrasera cette valeur par sa propre
  // logique → on la met aussi dans le metadata `regBlock` pour fallback.
  return layers.map(l => ({
    ...l,
    layerColor: REG_COLOR,
    regBlock: {
      ...l.regBlock,
      zones: !!zones,
      ...(avertissementsAttendus ? { avertissements_attendus: avertissementsAttendus } : {}),
    },
  }));
}


/** Helper : true si un calque appartient à un bloc réglementaire. */
export function isRegLayer(el) {
  return el && el.regBlock && typeof el.regBlock.blockId === 'string';
}


/** Helper : groupe les calques par regBlock.blockId. Renvoie { [blockId]: [layers] } */
export function groupByRegBlock(elements) {
  const out = {};
  for (const el of elements) {
    if (isRegLayer(el)) {
      const id = el.regBlock.blockId;
      if (!out[id]) out[id] = [];
      out[id].push(el);
    }
  }
  return out;
}
